/**
 * ABALLE — Email Capture Popup
   * IIFE: engagement-based trigger, real form submit to /contact
   * using Shopify's native storefront form protection,
 * focus trapping, accessible dialog.
 *
 * Frequency: once per session. Subscribers suppressed permanently.
 * Mobile: uses bottom banner (< 40% viewport) to avoid Google interstitial penalty.
 */
(function () {
  'use strict';

  /* ── Gate: skip in theme editor ── */
  if (window.Shopify && window.Shopify.designMode) return;

  /* ── Gate: suppressed on the ARTIFACT 001 drop page ONLY.
     The page's own section sets window.ABALLE_SUPPRESS_EMAIL_POPUP (template-
     scoped, so it exists nowhere else); the exact-path check is a fallback.
     Every other page is unaffected. ── */
  if (window.ABALLE_SUPPRESS_EMAIL_POPUP) return;
  try {
    if (window.location.pathname.replace(/\/+$/, '') === '/pages/artifact-001') return;
  } catch (e) { /* continue */ }

  /* ── Gate: skip for QA captures / performance audits ── */
  try {
    if (new URLSearchParams(window.location.search).has('codex_audit')) return;
  } catch (e) { /* continue */ }

  /* ── Gate: successful Shopify customer form return ── */
  try {
    if (new URLSearchParams(window.location.search).get('customer_posted') === 'true') {
      localStorage.setItem('aballe_popup_subscribed', '1');

      /* Log the capture to the first-party collector (with the email hash) on
         this clean page load — reliable, no navigation race. Deferred via
         setTimeout because aballe-events.js (which defines window.abHash /
         window.abTrackLead) loads AFTER this script; the timer fires once both
         defer scripts have executed. */
      window.setTimeout(function () {
        try {
          var pendingEmail = sessionStorage.getItem('aballe_pending_capture');
          if (!pendingEmail) return;
          sessionStorage.removeItem('aballe_pending_capture');
          sessionStorage.removeItem('aballe_pending_capture_attribution');
          var fireCollector = function (h) {
            if (typeof window.abTrackLead === 'function') {
              window.abTrackLead('collector_email_capture', {
                capture_surface: 'popup', lead_type: 'email', email_sha256: h || null
              });
            }
          };
          if (typeof window.abHash === 'function') { window.abHash(pendingEmail).then(fireCollector); }
          else { fireCollector(null); }
        } catch (e3) { /* ok */ }
      }, 0);

      if (window.AballeAnalytics && typeof window.AballeAnalytics.track === 'function') {
        window.AballeAnalytics.track('collector_email_capture', {
          form_location: 'popup',
          form_context: 'collector_email',
        });
      }

      return;
    }
  } catch (e) { /* continue */ }

  /* ── Gate: already subscribed — suppress permanently ── */
  try {
    if (localStorage.getItem('aballe_popup_subscribed')) return;
  } catch (e) { /* private browsing — continue */ }

  /* ── Gate: already shown this session ── */
  try {
    if (sessionStorage.getItem('aballe_popup_shown')) return;
  } catch (e) { /* continue */ }

  /* ── Detect mobile ── */
  var isMobile = window.innerWidth <= 749;
  var path = window.location.pathname || '/';
  var isProduct = /^\/products\//.test(path);
  var isCart = /^\/cart/.test(path);
  /* Product & cart pages show on desktop exit-intent ONLY (see Trigger below),
     so the popup never interrupts an active purchase. Home & collections are
     aggressive: 6s / 20% scroll / exit-intent. No first-pageview gate. */
  var isExitOnly = isProduct || isCart;
  /* Mobile has no exit-intent, so product pages historically showed NOTHING on
     mobile — and ABALLE traffic is mostly mobile/IG landing on product pages,
     so the majority of shoppers were never asked for email (the mobile buyers
     who add to cart and leave). Give mobile PRODUCT pages a gentle, later
     bottom-banner (scroll/timer below) — never the /cart page (protect active
     checkout), never desktop (unchanged: exit-intent only). SEO-safe: the
     mobile banner is <40% viewport, not an interstitial. */
  var mobileProductCapture = isMobile && isProduct;

  /* ── State ── */
  var shown = false;
  var overlay = null;
  var cookieRetries = 0;

  /* Never stack on top of the region-limited cookie-consent banner. Returns
     true only while Shopify's privacy banner is actually on screen — a no-op
     for US traffic, where the banner is region-excluded. */
  function cookieBannerVisible() {
    var b = document.querySelector('#shopify-pc__banner, .shopify-pc__banner, [id*="cookie-banner"], [class*="cookie-banner"]');
    if (!b) return false;
    try {
      if (window.getComputedStyle(b).display === 'none') return false;
      var r = b.getBoundingClientRect();
      return r.height > 0 && r.bottom > 0 && r.top < window.innerHeight;
    } catch (e) { return false; }
  }

  /* ── Build DOM ── */
  function buildPopup() {
    overlay = document.createElement('div');
    overlay.className = 'email-popup-overlay' + (isMobile ? ' email-popup-mobile' : '');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Email signup');

    var panel = document.createElement('div');
    panel.className = 'email-popup-panel';

    /* Real <form> so Shopify's captcha script can bind to it.
       action="/contact", method="post". */
    panel.innerHTML =
      '<button type="button" class="email-popup-close" aria-label="Close">&times;</button>' +
      '<h2 class="email-popup-headline">FIRST ACCESS</h2>' +
      '<p class="email-popup-subhead">New collections. Before anyone else.</p>' +
      '<p class="email-popup-body">Limited production runs. Private viewing appointments. No noise. Only what matters.</p>' +
      '<form class="email-popup-form" action="/contact" method="post" accept-charset="UTF-8" data-aballe-track-manual="true">' +
        '<input type="hidden" name="form_type" value="customer">' +
        '<input type="hidden" name="utf8" value="\u2713">' +
        '<input type="hidden" name="contact[tags]" value="newsletter,popup_signup">' +
        '<input type="hidden" name="return_to" value="/?customer_posted=true">' +
        '<input type="email" name="contact[email]" class="email-popup-input" placeholder="your@email.com" aria-label="Email address" required autocomplete="email" autocorrect="off" autocapitalize="off">' +
        '<button type="submit" class="email-popup-submit">&rarr; GET FIRST ACCESS</button>' +
      '</form>' +
      '<p class="email-popup-message" role="status" aria-live="polite"></p>';

    if (window.AballeAttribution) {
      var tagInput = panel.querySelector('input[name="contact[tags]"]');
      var returnInput = panel.querySelector('input[name="return_to"]');
      if (tagInput) tagInput.value = window.AballeAttribution.tagString(['newsletter', 'popup_signup']);
      if (returnInput) returnInput.value = window.AballeAttribution.decorateReturnTo('/?customer_posted=true');
    }

    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    /* Wire events */
    overlay.querySelector('.email-popup-close').addEventListener('click', dismiss);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) dismiss();
    });
    document.addEventListener('keydown', onEscKey);

    /* Listen for form submit — validate, then let it through for captcha */
    overlay.querySelector('.email-popup-form').addEventListener('submit', onSubmit);
  }

  /* ── Show ── */
  function show() {
    if (shown) return;
    /* Late gate: page-scoped suppression flag (ARTIFACT 001) */
    if (window.ABALLE_SUPPRESS_EMAIL_POPUP) { clearTriggers(); return; }
    /* Defer while the cookie-consent banner is on screen so we never show two
       bottom banners at once. No-op for US traffic. Fail-safe: after ~30s,
       proceed anyway so the popup is never permanently suppressed. */
    if (cookieBannerVisible() && cookieRetries < 30) {
      cookieRetries++;
      window.setTimeout(show, 1000);
      return;
    }
    shown = true;

    /* Mark shown for this session */
    try { sessionStorage.setItem('aballe_popup_shown', '1'); } catch (e) { /* ok */ }

    buildPopup();

    /* Trigger reflow then add class for transition */
    overlay.offsetHeight; // eslint-disable-line no-unused-expressions
    overlay.classList.add('is-visible');

    /* Only lock scroll on desktop (mobile banner doesn't need it) */
    if (!isMobile) {
      document.body.classList.add('overflow-hidden');
    }

    /* Focus the input, trap focus */
    var input = overlay.querySelector('.email-popup-input');
    setTimeout(function () {
      input.focus();
      if (typeof trapFocus === 'function') {
        trapFocus(overlay, input);
      }
    }, 100);

  }

  /* ── Dismiss (X, click-outside, Escape) ── */
  function dismiss() {
    if (!overlay) return;
    close();
  }

  function close() {
    if (!overlay) return;
    overlay.classList.remove('is-visible');
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', onEscKey);

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus();
    }

    /* Remove after transition */
    setTimeout(function () {
      if (overlay && overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
      overlay = null;
    }, 400);
  }

  function onEscKey(e) {
    if (e.key === 'Escape') dismiss();
  }

  /* ── Form submit ──
     Validate, identify the contact in OmniSend, stash the email so the clean
     return page can log the capture to the first-party collector (with the
     email hash), then let the form submit NATURALLY — so Shopify's hCaptcha
     still binds/protects it. Logging on the return page (not here) avoids the
     navigation race that previously dropped every popup capture: SubtleCrypto's
     hash resolves after the page has already started unloading. */
  function onSubmit(e) {
    var input = overlay.querySelector('.email-popup-input');
    var email = input.value.trim();
    var btn = overlay.querySelector('.email-popup-submit');

    /* Validate — block submission if invalid */
    if (!email || !input.validity.valid) {
      e.preventDefault();
      input.classList.add('has-error');
      input.focus();
      return;
    }

    input.classList.remove('has-error');
    showMessage('');
    btn.textContent = '...';

    /* Stash for the return page (synchronous, so it survives navigation;
       cleared immediately once the capture is logged). */
    try { sessionStorage.setItem('aballe_pending_capture', email); } catch (e2) { /* ok */ }
    try {
      if (window.AballeAttribution) {
        sessionStorage.setItem('aballe_pending_capture_attribution', JSON.stringify(window.AballeAttribution.get()));
      }
    } catch (e3) { /* ok */ }

    /* ── OmniSend: identify contact via their JS tracking API ── */
    if (window.AballeAttribution && window.AballeAttribution.identifyContact(email, ['newsletter', 'popup_signup'], {
      capture_surface: 'popup',
      form_context: 'collector_email'
    })) {
      return;
    }

    if (window.omnisend && typeof window.omnisend.identifyContact === 'function') {
      window.omnisend.identifyContact({
        email: email,
        tags: ['newsletter', 'popup_signup']
      });
    }
    /* No preventDefault — let Shopify's protected (hCaptcha) form submit and
       redirect to /?customer_posted=true, where the capture is logged. */
  }

  function showMessage(message) {
    if (!overlay) return;

    var messageEl = overlay.querySelector('.email-popup-message');
    if (!messageEl) return;

    messageEl.textContent = message || '';
    messageEl.classList.remove('is-error');
  }

  /* ── Trigger ──
     Home & collections: aggressive — fires on 6s OR 20% scroll OR desktop
     exit-intent (whichever comes first). Product & cart: desktop exit-intent
     ONLY, so the popup never interrupts an active purchase. Mobile has no
     exit-intent, so exit-only pages simply won't show on mobile (intentional —
     protects mobile checkout). Mobile home/collections still use the 6s/20%
     bottom banner, which is not an intrusive interstitial (SEO-safe). */
  var delay = 6000;
  var scrollThreshold = 0.20;
  /* Mobile product pages: hold back and require real engagement so we never
     interrupt someone actively reading a product — later timer, deeper scroll. */
  if (mobileProductCapture) {
    delay = 15000;
    scrollThreshold = 0.5;
  }
  var timer = null;

  function hasScrolledPast(threshold) {
    var doc = document.documentElement;
    var maxScroll = Math.max(0, doc.scrollHeight - window.innerHeight);
    if (maxScroll < 240) return false;
    return (window.scrollY / maxScroll) >= threshold;
  }

  function clearTriggers() {
    if (timer) {
      window.clearTimeout(timer);
      timer = null;
    }
    window.removeEventListener('scroll', onScrollTrigger);
    document.removeEventListener('mouseout', onExitIntent);
  }

  function onScrollTrigger() {
    if (!hasScrolledPast(scrollThreshold)) return;
    clearTriggers();
    show();
  }

  /* Desktop exit-intent: cursor exits through the top edge of the viewport. */
  function onExitIntent(e) {
    if (isMobile) return;
    if (e.clientY > 0) return;                  // only when leaving via the top
    if (e.relatedTarget || e.toElement) return; // ignore moving into a child node
    clearTriggers();
    show();
  }

  if (!isMobile) {
    document.addEventListener('mouseout', onExitIntent);
  }

  if (!isExitOnly || mobileProductCapture) {
    timer = window.setTimeout(function () {
      clearTriggers();
      show();
    }, delay);
    window.addEventListener('scroll', onScrollTrigger, { passive: true });
    window.setTimeout(onScrollTrigger, 250);
  }
})();
